import { User } from '@supabase/supabase-js';
import { action, makeObservable, observable } from 'mobx';
import {
    PaymentProvider,
    cancelPaymentUserSubscription,
    checkPaymentUserSubscription,
    extendPaymentUserSubscription,
    fetchPaymentUserInfo,
} from 'http-client/payment.client';
import { RootStore } from './root.store';
import { UserProfile } from 'types/user';
import { getUserMetadata, updateWebSession } from 'http-client/session.client';

export interface UserSubscription {
    isPremium: boolean;
    info?: SubscriptionInfo;
}

export enum OriginalSubscriptionStatus {
    CANCELED = 'CANCELED',
    ACTIVE = 'ACTIVE',
}

export enum SubscriptionType {
    SUBSCRIPTION_WITH_TRIAL = 'SUBSCRIPTION WITH TRIAL',
    SUBSCRIPTION = 'SUBSCRIPTION',
}

export interface SubscriptionInfo {
    id: string;
    productId: string;
    createdAt: string;
    lastPaymentDay: string;
    nextPaymentDay: string;
    scheduleId: any;
    originalStatus: OriginalSubscriptionStatus;
    subscriptionId: string;
    provider: PaymentProvider;
    type: SubscriptionType;
    subscriptionStatus: string;
    isSubscriptionTrialing: boolean;
}

export type IpInfoRecordData = {
    ip: string | null;
    postal: string | null;
    country: string | null;
    city: string | null;
    loc: string | null;
    timezone: string | null;
    state?: string | null;
};

export type UserWebSession = {
    [key: string]: any;
    session?: {
        userId?: string;
        flowOuter?: boolean;
        trialGtagSent?: boolean;
        gclid?: string | null;
        ipInfo?: IpInfoRecordData | null;
        user?: Record<string, any> | null;
        abTestData?: Record<string, any> | null;
        ampliAttributesUpdated?: boolean | null;
        webAttributionSent?: boolean | null;
        quiz?: Record<string, any> | null;
    };
};

export class UserStore {
    rootStore: RootStore;
    user: User | null = null;
    userProfile: UserProfile | null = null;
    userSubscription: UserSubscription | null = null;
    userWebSession: UserWebSession | null = null;
    subscriptionLoading = false;
    webSessionLoading = true;

    constructor(rootStore: RootStore) {
        makeObservable(this, {
            user: observable,
            userProfile: observable,
            userSubscription: observable,
            userWebSession: observable,
            subscriptionLoading: observable,
            webSessionLoading: observable,
            setUser: action,
            setUserProfile: action,
            setUserSubscription: action,
            setSubscriptionLoading: action,
            setWebSession: action,
            setWebSessionLoading: action,
        });
        this.rootStore = rootStore;
    }

    setUserProfile = (profile: UserProfile | null) => {
        this.userProfile = profile;
    };

    setUserSubscription = (subscription: UserSubscription | null) => {
        this.userSubscription = subscription;
    };

    setSubscriptionLoading = (loading: boolean) => {
        this.subscriptionLoading = loading;
    };

    setWebSessionLoading = (loading: boolean) => {
        this.webSessionLoading = loading;
    };

    setWebSession = (session: UserWebSession | null) => {
        this.userWebSession = session;
    };

    updateUserWebSession = async (session: Record<string, any>) => {
        const user = await updateWebSession(session);
        this.setUser(user);
    };

    setUser = (user: User | null) => {
        this.user = user;

        if (!user) {
            this.setWebSession(null);
        }

        const userMetaData = user?.user_metadata;
        this.setWebSession(userMetaData || null);
    };

    fetchUserProfile = async (email?: string | null | undefined) => {
        let profile: UserProfile | null = null;

        if (email) {
            profile = await fetchPaymentUserInfo(email);
        }

        this.setUserProfile(profile);
    };

    fetchUserSession = async (): Promise<UserWebSession | null> => {
        this.setWebSessionLoading(true);

        const session = await getUserMetadata();
        this.setWebSession(session);

        /* Set here any Mobx observables that depend on Web session */
        const sessionFlowOuter = session?.session?.flowOuter;
        if (sessionFlowOuter != null) {
            this.rootStore.appStore.setFlowOuter(sessionFlowOuter);
        }

        this.setWebSessionLoading(false);
        return session;
    };

    checkUserSubscription = async (): Promise<UserSubscription | null> => {
        const email = this.user?.email;
        let data: UserSubscription | null = null;

        if (email) {
            this.setSubscriptionLoading(true);
            data = await checkPaymentUserSubscription(email);
            this.setUserSubscription(data);
            this.setSubscriptionLoading(false);
        } else {
            this.setUserSubscription(null);
        }

        return data;
    };

    cancelUserSubscription = async (subscriptionId: string) => {
        const provider = this.userSubscription?.info?.provider;
        if (!provider) throw new Error('No payment provider found');

        await cancelPaymentUserSubscription(subscriptionId, provider);
    };

    extendUserSubscription = async (subscriptionId: string) => {
        const provider = this.userSubscription?.info?.provider;
        if (!provider) throw new Error('No payment provider found');

        await extendPaymentUserSubscription(subscriptionId, provider);
    };

    hydrate = (data: any) => {
        if (!data) {
            return;
        }
        if (data.user) {
            this.user = data.user;
        }
    };
}
